import { useEffect, useMemo } from 'react';
import { Marked, type Tokens } from 'marked';
// Fuente única del manual en cada idioma: los mismos Markdown del repositorio de los que se generan los Word.
import manualEs from '../../../../docs/manual-usuario.md?raw';
import manualEn from '../../../../docs/en/user-manual.md?raw';
import { PageHeader } from '../components/ui';
import { useI18n, type Lang } from '../i18n';

const MANUALS: Record<Lang, { md: string; docx: string; docxName: string }> = {
  es: { md: manualEs, docx: '/manual-usuario.docx', docxName: 'Manual de usuario - Voxel Licencias.docx' },
  en: { md: manualEn, docx: '/user-manual.docx', docxName: 'User manual - Voxel Licensing.docx' },
};

/** Anclas estilo GitHub, para que funcionen los enlaces del índice del propio Markdown. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/<[^>]+>/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s/g, '-');
}

const plain = (s: string) => s.replace(/[*_`]/g, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');

/** Quita lo que en el panel sobra: selector de idioma, título, nota para desarrolladores e índice. */
export function stripForPanel(raw: string): string {
  const md = raw.replace(/\r\n/g, '\n');
  const cut = md.indexOf('\n## ');
  // Cabecera (antes del primer capítulo): la nota citada es para desarrolladores.
  const head = (cut >= 0 ? md.slice(0, cut + 1) : md)
    .replace(/^\*\*[^*\n]+\*\*\s·\s\[[^\]]+\]\([^)]+\)\s*\n/, '')
    .replace(/^# .*\n/m, '')
    .replace(/^>.*\n(?:>.*\n)*/gm, '');
  const body = cut >= 0 ? md.slice(cut + 1) : '';
  return head + body.replace(/^## (Índice|Contents|Table of contents)\n[\s\S]*?\n---\n/m, '');
}

export function renderManual(md: string) {
  const toc: { id: string; text: string }[] = [];
  const marked = new Marked({
    gfm: true,
    renderer: {
      heading({ tokens, depth, text }: Tokens.Heading) {
        const id = slugify(plain(text));
        if (depth === 2) toc.push({ id, text: plain(text) });
        return `<h${depth} id="${id}">${this.parser.parseInline(tokens)}</h${depth}>\n`;
      },
      link({ href, tokens }: Tokens.Link) {
        const inner = this.parser.parseInline(tokens);
        if (href.startsWith('#')) return `<a href="${href}">${inner}</a>`;
        // Documentos técnicos del repositorio (.md): en el panel se muestran como texto.
        if (!/^https?:\/\//.test(href)) return `<span>${inner}</span>`;
        return `<a href="${href}" target="_blank" rel="noreferrer">${inner}</a>`;
      },
    },
  });
  const html = marked.parse(stripForPanel(md), { async: false });
  return { html, toc };
}

export function ManualPage() {
  const { t, lang } = useI18n();
  const manual = MANUALS[lang];
  const { html, toc } = useMemo(() => renderManual(manual.md), [manual.md]);

  // Al entrar con un ancla (#6-licencias) se desplaza a esa sección.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [html]);

  return (
    <>
      <PageHeader
        title={t('manual.title')}
        subtitle={t('manual.subtitle')}
        actions={
          <>
            <button className="btn" onClick={() => window.print()}>
              {t('manual.print')}
            </button>
            <a className="btn btn-primary" href={manual.docx} download={manual.docxName}>
              {t('manual.downloadWord')}
            </a>
          </>
        }
      />
      <div className="manual-layout">
        <nav className="card manual-toc" aria-label={t('manual.contents')}>
          <div className="nav-section" style={{ paddingTop: 0, paddingLeft: 0 }}>
            {t('manual.contents')}
          </div>
          {toc.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.text}
            </a>
          ))}
        </nav>
        {/* Contenido propio del repositorio (no introducido por usuarios), por eso se inyecta como HTML. */}
        <article className="card manual" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </>
  );
}
